import * as React from "react";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { Column, Divider, Stack, Text } from "@origin-digital/ods-core";
import { Autocomplete } from "@origin-digital/ods-lab";
import { IFormProps } from "../FormAccordionContainer";
import { FormikErrors } from "formik";
import { Companies, Company } from "src/components/Projects/types";
import { apiClient } from "src/api/apiService";
import FormContext from "src/context/FormContext";
import type {
  RequestingCompanyResponse,
  RequestingCompany as RequestingCompanyType,
} from "src/components/Form/types";
import { AppContext } from "src/context/AppContext";
import { fromCompanyToRequestingCompany } from "../helpers";

interface IRequestingCompanyProps extends IFormProps {
  fieldName?: string;
  error:
    | string
    | string[]
    | FormikErrors<any>
    | FormikErrors<any>[]
    | undefined;
  isEditing: boolean;
  requestingCompany?: RequestingCompanyType | null | RequestingCompanyResponse;
  fullWidth?: boolean;
  setNoCompany?: () => void;
}

const RequestingCompanyWrapper: React.FC<{
  fullWidth: boolean;
  children: React.ReactNode;
}> = ({ fullWidth, children }) => {
  return fullWidth ? <>{children}</> : <Column>{children}</Column>;
};

const RequestingCompany = (props: IRequestingCompanyProps) => {
  const [inputValue, setInputValue] = React.useState<string>("");
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>("");
  const { setFormState } = React.useContext(FormContext);
  const {
    context: { user },
  } = React.useContext(AppContext);
  const {
    setFieldValue,
    error,
    isEditing,
    requestingCompany,
    fullWidth,
    setNoCompany,
    fieldName,
  } = props;
  const [requestingCompanies, setRequestingCompanies] = useState<
    {
      label: string;
      value: RequestingCompanyType | undefined;
    }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [singleCompany, setSingleCompany] =
    useState<RequestingCompanyType | null>(null);

  useDebounce(
    () => {
      setDebouncedInputValue(inputValue);
    },
    300,
    [inputValue]
  );

  useEffect(() => {
    if (requestingCompany === undefined) {
      setFieldValue(
        "requestingCompany",
        fromCompanyToRequestingCompany(user?.main_company)
      );
    } else {
      setFieldValue("requestingCompany", requestingCompany);
    }
  }, []);
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get<Companies>("/company/companies", {
          params: debouncedInputValue
            ? {
                name: debouncedInputValue,
              }
            : {},
        });

        // Transform companies data
        const transformedCompanies = response.data.companies
          .map((company: Company) => ({
            label: company.name,
            value: fromCompanyToRequestingCompany(company),
          }))
          .filter((item) => item.value !== undefined);

        // Check if we have exactly one company
        if (transformedCompanies.length === 1 && debouncedInputValue === "") {
          setSingleCompany(transformedCompanies[0].value || null);
          setFieldValue("requestingCompany", transformedCompanies[0].value);
        } else {
          setSingleCompany(null);
        }

        if (transformedCompanies.length === 0 && debouncedInputValue === "") {
          setNoCompany?.();
        }

        setRequestingCompanies(transformedCompanies);
        setFormState((prevState) => ({
          ...prevState,
          requestingCompanies: response.data.companies
            .map((company: Company) => fromCompanyToRequestingCompany(company))
            .filter(
              (company) => company !== undefined
            ) as RequestingCompanyType[], // Filter out undefined values and cast
        }));
        setLoading(false);
      } catch (err) {
        console.error("Failed to fetch companies:", err);
        setLoading(false);
      }
    };

    fetchCompanies();
  }, [debouncedInputValue]);

  return (
    <RequestingCompanyWrapper fullWidth={!!fullWidth}>
      {isEditing ? (
        <ReadOnlyRequestingCompany
          requestingCompanyName={requestingCompany?.name || ""}
        />
      ) : singleCompany ? (
        <ReadOnlyRequestingCompany
          requestingCompanyName={singleCompany.name || ""}
        />
      ) : (
        <Autocomplete
          id="requesting-company"
          textFieldProps={{
            variant: "outlined",
            hideHelperTextSpace: true,
          }}
          label={fieldName ? fieldName : "Requesting company"}
          options={requestingCompanies}
          value={
            requestingCompanies.find(
              (company) => company?.value?.id === requestingCompany?.id
            ) || null
          }
          onChange={(_event, item, reason) => {
            if (reason === "clear") {
              // if we set them as empty string, the component behaves weird
              setFieldValue("requestingCompany", null);
            } else {
              setFieldValue("requestingCompany", item!.value);
            }
          }}
          inputValue={inputValue}
          onInputChange={(_event, tempValue, reason) => {
            if (reason === "clear") {
              return setInputValue("");
            }
            setInputValue(tempValue);
          }}
          error={!!error}
          helperText={error as string}
          loading={loading}
        />
      )}
    </RequestingCompanyWrapper>
  );
};

const ReadOnlyRequestingCompany = (props: {
  requestingCompanyName: string;
}) => {
  const { requestingCompanyName } = props;
  return (
    <Stack space="xsmall">
      <Stack space="xxsmall">
        <Text variant="body-small" tone="neutralLight">
          Requesting company
        </Text>
        <Text data-role="value" data-id={"requestingCompanyReadOnly"}>
          {requestingCompanyName}
        </Text>
      </Stack>
      <Divider />
    </Stack>
  );
};

export default RequestingCompany;
